// Per spec/blocks.md §sub-section-heading. **P-037.9 (D-109)**: smaller
// sub-section heading used inside / between sections (e.g., state callouts
// inside an events list, minor content separators). Distinct from
// `section-heading` in three ways:
//
// 1. Typography: 14 px Helvetica bold `#666666` (`<h3>` — sub-section
//    nests beneath surrounding h2s)
// 2. Rule padded 30 px L/R (not full-width like SectionHeader)
// 3. Optional state flag rendered to the left of the heading. The user
//    selects a state via dropdown; the Block looks up the flag image URL
//    from the state registry. `'none'` is the no-flag sentinel.
//
// CTA fields are independently optional (D-108 pattern relaxed at the
// Block layer); render forwards atomic CTA to the Component only when
// both `ctaLabel` and `ctaUrl` are present.

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { subSectionHeader } from '../components/sub-section-header'
import { states, STATE_IDS } from '../registry/states'

// `'none'` sentinel + the 7 state ids. Z.enum requires `[string, ...string[]]`
// — spreading the readonly STATE_IDS tuple into a fresh tuple with `'none'`
// at the head produces that shape. `as const` preserves the literal-string
// union narrowing through the `z.infer` round-trip.
const STATE_OR_NONE = ['none', ...STATE_IDS] as const

const optionsSchema = z.object({
  state: z.enum(STATE_OR_NONE),
})

const inputsSchema = z.object({
  heading: z.string().min(1),
  ctaLabel: z.string().min(1).optional(),
  ctaUrl: z.string().url().optional(),
})

export const subSectionHeading = defineBlock({
  id: 'sub-section-heading',
  label: 'Sub Section Heading',
  category: 'meta',
  optionsSchema,
  inputsSchema,
  defaultOptions: { state: 'none' },
  defaultInputs: { heading: 'Sub Section Heading' },
  render: ({ options, inputs }, ctx) => {
    const flagImageUrl =
      options.state !== 'none' ? states[options.state].flagImg : undefined
    // P-037.18 D-118: thread ctx.linkColor through so the CTA arm renders
    // in the template-level link color when both ctaLabel + ctaUrl are set.
    //
    // P-037.31 fourth post-Phase-Close §D (big-3 → sub-section-heading
    // auto-collapse): when this Block follows `big3-signature-events`,
    // pass `hideTopRule: true` so SubSectionHeader drops the divider
    // rule. Pairs with the `PAIR_OVERRIDES` entry in `block-padding.ts`
    // that collapses the system inter-Block spacer to 0 for the same
    // pairing.
    //
    // P-037.30 ninth post-Phase-Close §C — Banks's correction: the
    // fourth-batch version ALSO passed `noTopRule: true` which (under
    // the old bundled boolean) ALSO dropped the 30 px PAD_TOP above
    // the heading text. Banks's preview-walk: that was too aggressive
    // — the heading text was ending up flush against big-3's tinted
    // footer edge with no white-space gap. Fix: pass `hideTopRule:
    // true` only (the new orthogonal flag), so the rule disappears but
    // the 30 px PAD_TOP remains. The big-3 baked "Big 3 Meetings"
    // header keeps both flags on per its different design intent (sit
    // flush against the Block's internal 20 px top pad).
    const hideTopRule = ctx.precedingBlockId === 'big3-signature-events'

    if (inputs.ctaLabel === undefined || inputs.ctaUrl === undefined) {
      return subSectionHeader({
        heading: inputs.heading,
        linkColor: ctx.linkColor,
        ...(flagImageUrl !== undefined ? { flagImageUrl } : {}),
        ...(hideTopRule ? { hideTopRule: true } : {}),
      })
    }
    return subSectionHeader({
      heading: inputs.heading,
      ctaLabel: inputs.ctaLabel,
      ctaUrl: inputs.ctaUrl,
      linkColor: ctx.linkColor,
      ...(flagImageUrl !== undefined ? { flagImageUrl } : {}),
      ...(hideTopRule ? { hideTopRule: true } : {}),
    })
  },
})
